import "../i18n";
import { Sentry } from "../sentryInstrumentReact";
import React, { useState, useEffect } from 'react';

import AddressFormInput from './AddressFormInput';
import AddressFormSelect from './AddressFormSelect';
import { useCart } from './CartContext';
import countryRegionDataJSON from "../../data/countryRegion.json";

function AddressForm({ type }) {
  const { checkoutFormData, setCheckoutFormData, checkoutErrors, shippingRequired } = useCart();
  const [canadaShippingNotice, setCanadaShippingNotice] = useState('');
  const [countryRegionData, setCountryRegionData] = useState([]);

  // fetch country data
  useEffect(() => {
    async function fetchCountryAndRegions(retries) {
	  let response;
      try {
        response = await fetch("/geo/_api/countries/", {
          method: 'GET',
          headers: {"Content-Type": "application/json"},
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        if ((type === "shipping") || (shippingRequired)) {
          const filteredData = data.filter((country) => country.code == "USA" || country.code == "CA");
          setCountryRegionData(filteredData);
        } else {
          setCountryRegionData(data);
        }

      } catch (error) {

        if (retries > 0) {
          await new Promise(r => setTimeout(r, 1000));	// NOTE: sleep 1 second
          return await fetchCountryAndRegions(retries - 1);
        } else {
          if ((type === "shipping") || (shippingRequired)) {
            const filteredData = countryRegionDataJSON.filter((country) => country.code == "USA" || country.code == "CA");
            setCountryRegionData(filteredData);
          } else {
            setCountryRegionData(countryRegionDataJSON);
          }
        }
		if (Sentry && Sentry.getClient()) {
          Sentry.captureException(error, {retries: retries, stage: "fetchCountryAndRegions", response: response});
		}
      }
    }
    fetchCountryAndRegions(4);
  }, []);

  // set up autocomplete
  useEffect(() => {
    for (const addrType of ["billing", "shipping"]) {
      let street1Input = document.getElementById(`${addrType}_street1`);
	  if (street1Input === null) {
		continue;
	  }
      let autoComplete = new google.maps.places.Autocomplete(street1Input, {types: ["address"]});

	  if (shippingRequired) {
		autoComplete.setComponentRestrictions({
		  country: ["us", "ca"],
		});
	  }
      autoComplete.addListener("place_changed", event => {
        const place = autoComplete.getPlace();

        if (!place || !Array.isArray(place.address_components)) {
          console.error("No address components found.");
          return;
        }
        
        const components = {};
        place.address_components.forEach(component => {
          component.types.forEach(type => {
            components[type] = component;
          });
        });

        let street1, street2 = "";
        if (components.subpremise && place.name.endsWith(components.subpremise.long_name)) {
          street1 = place.name.replace(components.subpremise.long_name, "").trim();
          street2 = components.subpremise.long_name;
        } else {
          street1 = place.name;
        }

        // set city
        let city = '';
        if (components.locality) {
          city = components.locality.short_name;
        } else if (components.administrative_area_level_2) {
          city = components.administrative_area_level_2.long_name;
        }
        
        // set postal code
        let postalCode = components.postal_code ? components.postal_code.long_name : ""
        if (postalCode !== "" && components.postal_code_suffix) {
          postalCode = `${postalCode}-${components.postal_code_suffix.long_name || components.postal_code_suffix.short_name}`;					 
        }
        const country = (components.country.short_name === "US") ? "USA" : components.country.short_name;
        const region = components.administrative_area_level_1.short_name;

        // proper way to update the formData
        setCheckoutFormData((prevFormData) => {

          return {
            ...prevFormData,
            [addrType]: {
              ...prevFormData[addrType],
              street1: street1,
              street2: street2,
              city: city,
              region: region,
              postalCode: postalCode,
              country: country,
              addressComplete: true,
            }
          };
        });
      }); 
    }
  }, []);


  useEffect(() => {
    async function fetchCanadaShippingFlatblock() {
      try {
        response = await fetch("/flatblocks/_api/flatblocks/canada-shipping-notice", {
          method: 'GET',
          headers: {"Content-Type": "application/json"},
        });
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        setCanadaShippingNotice(data.content);

      } catch (error) { 
        console.log(error);
      }
    }
    fetchCanadaShippingFlatblock();
  }, []);


  const handleChange = (e) => {
    const { name, value } = e.target;
    let isComplete;
    setCheckoutFormData((prevFormData) => {
      const updatedTypeData = {
        ...prevFormData[type],
        [name]: value,
      };  

      // minor form validation
      if (
        (
          (updatedTypeData.firstName && updatedTypeData.lastName) || 
          updatedTypeData.organization
        ) &&
        updatedTypeData.street1 &&
        updatedTypeData.city &&
        updatedTypeData.region &&
        updatedTypeData.postalCode.length > 4 &&
        updatedTypeData.country
      ) {
        isComplete = true;
      } else {
        isComplete = false;
      }

      // update checkoutFormData state
      return {
        ...prevFormData,
        [type]: {
          ...updatedTypeData,
          addressComplete: isComplete, 
        }
      }

    }); 
    
  };


  return (
    <>
      <div className="checkout-body shipping-information">
        <div className="form-grid-gap">
          <div className="form-group-row">
            <AddressFormInput 
              type="text"
              name="firstName"
              label={window.gettext("First Name")}
              value={checkoutFormData[type].firstName} 
              onChange={handleChange}
              maxLength="20"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.first_name ?? ""}
            />
            <AddressFormInput 
              type="text"
              name="lastName"
              label={window.gettext("Last Name")}
              value={checkoutFormData[type].lastName} 
              onChange={handleChange}
              maxLength="20"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.last_name ?? ""}
            />
          </div>
          {type === "billing" && 
            <div className="form-group-row">
              <AddressFormInput 
                type="email"
                name="email"
                label={window.gettext("Email")}
                value={checkoutFormData[type].email} 
                onChange={handleChange}
                maxLength="300"
                required
                idPrefix={type}
                errors={checkoutErrors?.[type]?.email ?? ""}
              />
              <AddressFormInput 
                type="tel"
                name="phone"
                label={window.gettext("Phone")}
                value={checkoutFormData[type].phone?.number} 
                onChange={handleChange}
                maxLength="25"
                required
                idPrefix={type}
                errors={checkoutErrors?.[type]?.phone ?? ""}
              />
            </div>
          }
          <div className="form-group-row">
            <AddressFormInput 
              type="text"
              name="organization"
              label={window.gettext("Organization Name")}
              value={checkoutFormData[type].organization} 
              onChange={handleChange}
              maxLength="70"
              idPrefix={type}
              errors={checkoutErrors?.[type]?.organization ?? ""}
            />
          </div>
          <AddressFormInput 
            type="text"
            name="street1"
            label={window.gettext("Address")}
            value={checkoutFormData[type].street1} 
            onChange={handleChange}
            maxLength="70"
            required
            idPrefix={type}
            errors={checkoutErrors?.[type]?.address1 ?? ""}
            placeholder=""
          />
          <AddressFormInput 
            type="text"
            name="street2"
            label={window.gettext("Address 2")}
            value={checkoutFormData[type].street2} 
            onChange={handleChange}
            maxLength="70"
            idPrefix={type}
            errors={checkoutErrors?.[type]?.address2 ?? ""}
          />
          <div className="form-group-row">
            <AddressFormSelect 
              containerDivId={`${type}_div_id_country`}
              labelText={window.gettext("Country")}
              optionsArray={[{value: '', label: window.gettext("-- Select --")}].concat(
                countryRegionData.map((country) => ({
                  value: country.code,
                  label: country.description,
                }))
              )}
              selectName="country"
              selectId={`${type}_div_id_country`}
              selectValue={checkoutFormData[type].country}
              onChange={handleChange}
              errors={type === 'billing' ? checkoutErrors.country : checkoutErrors.shipping_country}
            /> 
          </div>
          <div className="form-group-row">
            <AddressFormInput 
              type="text"
              name="city"
              label={window.gettext("City")}
              value={checkoutFormData[type].city} 
              onChange={handleChange}
              maxLength="70"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.city ?? ""}
            />
            <AddressFormSelect 
              containerDivId={`${type}_div_id_region`}
              labelText={window.gettext("State")}
              optionsArray={[{value: '', label: window.gettext("-- Select State --")}].concat(
                (countryRegionData.find((element) => element.code === checkoutFormData[type].country)?.regions || [])
                .map(region => ({
                  value: region.id,
                  label: region.description,
                }))
              )}
              selectName="region"
              selectId={`${type}_div_id_region`}
              selectValue={checkoutFormData[type].region}
              onChange={handleChange}
              errors={type === 'billing' ? checkoutErrors.region : checkoutErrors.shipping_region}
            /> 
            <AddressFormInput 
              type="text"
              name="postalCode"
              label={window.gettext("Zip/Postal Code")}
              value={checkoutFormData[type].postalCode} 
              onChange={handleChange}
              maxLength="15"
              required
              idPrefix={type}
              errors={checkoutErrors?.[type]?.post ?? ""}
            />
			      <AddressFormInput
              type="hidden"
              name="autocompleteDigest"
              value={checkoutFormData[type].autocompleteDigest}
              idPrefix={type}
            />
          </div>
          {checkoutFormData[type].country == "CA" &&
            <div className="shipping-msg">
              <p>{canadaShippingNotice}</p>
            </div>
          }
        </div>
      </div>
    </>
  );

}

export default AddressForm;
